import { useState, useEffect, useContext } from 'react';
import { Alert } from 'reactstrap';
import { RxCrossCircled } from 'react-icons/rx';
import parse, {
    DOMNode,
    Element,
    HTMLReactParserOptions,
    attributesToProps,
    domToReact,
} from 'html-react-parser';
import { getDashColor } from '~/shared/components/icons';
import { CampaignContext } from '~/context/campaign';
import { fetchCampaignMessage } from '~/services/campaign';
import { ICampaignMessage } from '~/types/campaign';

// Holds the updated_at of the message the user dismissed, so a new or edited
// message shows again (HHLTB-2818).
const DISMISSAL_KEY = 'hhl_campaign_message_dismissed';

const parseOptions: HTMLReactParserOptions = {
    replace: (node) => {
        if (node instanceof Element && node.name === 'a') {
            return (
                <a
                    {...attributesToProps(node.attribs)}
                    style={{
                        color: 'white',
                        textDecoration: 'underline',
                        fontWeight: 'bold',
                    }}
                >
                    {domToReact(node.children as DOMNode[], parseOptions)}
                </a>
            );
        }
    },
};

// The announcement bar staff write under Settings > Campaign Message in the
// admin. It replaces the hardcoded Fund Requests card.
export const CampaignMessageCard = () => {
    const [{ campaign }] = useContext(CampaignContext);
    const [message, setMessage] = useState<ICampaignMessage | null>(null);
    const [dismissedVersion, setDismissedVersion] = useState<string | null>(
        () => localStorage.getItem(DISMISSAL_KEY)
    );

    useEffect(() => {
        let cancelled = false;
        fetchCampaignMessage()
            .then((resp) => {
                if (!cancelled) setMessage(resp);
            })
            .catch((error) => {
                console.error('Error fetching the campaign message:', error);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const statusCode = campaign?.campaignDetail?.campaign_status_code;
    if (
        !message ||
        !statusCode ||
        !message.status_codes.includes(statusCode) ||
        dismissedVersion === message.updated_at
    ) {
        return null;
    }

    const onDismiss = () => {
        localStorage.setItem(DISMISSAL_KEY, message.updated_at);
        setDismissedVersion(message.updated_at);
    };

    return (
        <Alert
            style={{
                borderTopRightRadius: '0',
                borderBottomRightRadius: '0',
                backgroundColor: getDashColor('message'),
                borderColor: getDashColor('message'),
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25)',
                color: 'white',
                position: 'relative',
            }}
        >
            <RxCrossCircled
                onClick={onDismiss}
                style={{
                    position: 'absolute',
                    top: '30px',
                    right: '25px',
                    fontSize: '1.5rem',
                    color: 'white',
                    cursor: 'pointer',
                }}
                title="Dismiss announcement"
            />
            {message.headline && (
                <div
                    className="eyebrow"
                    style={{
                        marginBottom: '18px',
                        color: 'white',
                    }}
                >
                    {message.headline}
                </div>
            )}
            {parse(message.body, parseOptions)}
        </Alert>
    );
};
